import React, { useEffect, useState } from "react";
import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    NumberInput,
    NumberInputField,
    Text,
    useToast,
    VStack,
} from "components-lib";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { API_ENDPOINTS, axiosInstance } from "config/api";
import { humanizeApiError } from "../../../utils/api_error_samples";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";
import ReactSelect from "react-select";
import FileUploadField from "../../../components/FileUploadField";
// import c from "config";

const indianLanguages = [
    "Hindi", "Bengali", "Telugu", "Marathi", "Tamil",
    "Urdu", "Gujarati", "Kannada", "Malayalam", "Odia",
];

export default function DoctorEditForm({ isOpen, onClose, doctor, onUpdate }) {
    const [formData, setFormData] = useState({
        id: "",
        doctor: "",
        slot_duration: "",
        opd_timing: {
            monday: "",
            tuesday: "",
            wednesday: "",
            thursday: "",
            friday: "",
            saturday: "",
            sunday: null
        },
        emergency_charge: "",
        tax_percentage: 0,
        payment_details: {
            account_holder: "",
            account_number: "",
            bank_name: "",
            ifsc_code: "",
            upi_id: "",
        },
        language: "",
        whatsapp_no: "",
        medical_license: "",
        qualification: "",
        services: [{ serviceName: "", unitPrice: "", discount: "" }],
        clinic_id: "",
        clinic_name: "", // Store clinic name for display
        selectedWeekdays: [],
        timingStart: "",
        timingEnd: "",
        isParameters: 0,
        speech_to_text: 0
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [originalDoctorData, setOriginalDoctorData] = useState(null);
    const [doctorParameters, setDoctorParameters] = useState([]); // fetched from API
    const [isLoadingServices, setIsLoadingServices] = useState(false);
    const [selectedParameters, setSelectedParameters] = useState([]); // for dropdown selection
    const [newPassword, setNewPassword] = useState();
    const [showPassword, setShowPassword] = useState();
    const toast = useToast();
    // Uploads state for existing/new files
    const [uploads, setUploads] = useState({
        doctor_signature: null,
        bar_code: null,
        qr_code: null,
        letterhead: null,
    });
    const [isUploadingFiles, setIsUploadingFiles] = useState(false);
    const [isUploadsCompleted, setIsUploadsCompleted] = useState(false);

    const fetchDoctorServices = async (doctorId) => {
        if (!doctorId) return;

        setIsLoadingServices(true);
        try {
            const response = await axiosInstance.get(
                `/api/doctor/profile?doctor_id=${doctorId}`,
            );
            const doctorData = response.data.doctor;

            if (doctorData) {
                // Extract services from the API response
                const services = [];
                const serviceNames = doctorData.service ? doctorData.service.split(',') : [];
                const unitPrices = doctorData.unit_price ? doctorData.unit_price.split(',').map(price => price.trim()) : [];
                const discounts = doctorData.discount ? doctorData.discount.split(',').map(discount => discount.trim()) : [];

                // Create service objects
                serviceNames.forEach((name, index) => {
                    services.push({
                        serviceName: name.trim(),
                        unitPrice: unitPrices[index] || '',
                        discount: discounts[index] || '0'
                    });
                });

                // Update form data with the fetched services
                setFormData(prev => ({
                    ...prev,
                    services: services.length > 0 ? services : [{ serviceName: '', unitPrice: '', discount: '' }]
                }));
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to load doctor services',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoadingServices(false);
        }
    };

    useEffect(() => {
        if (isOpen && doctor) {
            // Store original doctor data for preserving unchanged fields
            setOriginalDoctorData(doctor);

            // Extract doctor fields from nested structure if needed
            const doctorFields = doctor.doctor || doctor;

            // Fetch doctor services when the modal opens
            if (doctor.id) {
                fetchDoctorServices(doctor.id);
            }

            // Parse OPD timing from string format to object
            let opdTimings = {};
            try {
                const opdTimingSource = doctorFields.opd_timing || doctor.opdTiming;
                if (typeof opdTimingSource === 'string') {
                    opdTimings = JSON.parse(opdTimingSource);
                } else {
                    opdTimings = opdTimingSource || {};
                }
            } catch (error) {
                console.error('Error parsing OPD timing:', error);
                opdTimings = {};
            }

            // Get selected weekdays from OPD timing
            const selectedDays = Object.entries(opdTimings)
                .filter(([_, timing]) => timing && timing !== "null" && timing !== "")
                .map(([day]) => day);

            // Handle payment details - support both old and new format
            let paymentDetails = {
                account_holder: "",
                account_number: "",
                bank_name: "",
                ifsc_code: "",
                upi_id: "",
            };

            if (doctorFields.payment_details && typeof doctorFields.payment_details === 'object') {
                // New structured format
                paymentDetails = {
                    account_holder: doctorFields.payment_details.account_holder || "",
                    account_number: doctorFields.payment_details.account_number || "",
                    bank_name: doctorFields.payment_details.bank_name || "",
                    ifsc_code: doctorFields.payment_details.ifsc_code || "",
                    upi_id: doctorFields.payment_details.upi_id || "",
                };
            } else {
                // Old format - convert from upi_details and bank_details strings
                if (doctorFields.upi_details) {
                    paymentDetails.upi_id = doctorFields.upi_details;
                }
                if (doctorFields.bank_details) {
                    // Parse the old bank_details string to extract individual fields
                    // Format: "MH BANK, A/C: 996665558896, IFSC: SBIN0000454"
                    const bankDetailsStr = doctorFields.bank_details;

                    // Extract bank name (everything before the first comma)
                    const bankNameMatch = bankDetailsStr.match(/^([^,]+)/);
                    if (bankNameMatch) {
                        paymentDetails.bank_name = bankNameMatch[1].trim();
                    }

                    // Extract account number (A/C: followed by numbers)
                    const accountMatch = bankDetailsStr.match(/A\/C:\s*([^,]+)/);
                    if (accountMatch) {
                        paymentDetails.account_number = accountMatch[1].trim();
                    }

                    // Extract IFSC code (IFSC: followed by alphanumeric)
                    const ifscMatch = bankDetailsStr.match(/IFSC:\s*([^,]+)/);
                    if (ifscMatch) {
                        paymentDetails.ifsc_code = ifscMatch[1].trim();
                    }

                    // Extract account holder (Holder: followed by name)
                    const holderMatch = bankDetailsStr.match(/Holder:\s*([^,]+)/);
                    if (holderMatch) {
                        paymentDetails.account_holder = holderMatch[1].trim();
                    }
                }
            }

            // Extract clinic information from the API response
            const clinicInfo = doctor.clinic || {};

            // Set form data
            setFormData({
                id: doctorFields.id || doctor.id,
                doctor: doctorFields.doctor || doctor.doctorName || "",
                slot_duration: doctorFields.slot_duration || "",
                opd_timing: opdTimings,
                emergency_charge: doctorFields.emergency_charge || doctor.emergencyCharge || "",
                outside_opd_charge: doctorFields.outside_opd_charge || doctor.outside_opd_charge || "",
                payment_details: paymentDetails,
                language: Array.isArray(doctorFields.language) ? doctorFields.language.join(", ") :
                    (doctorFields.language || (Array.isArray(doctor.languages) ? doctor.languages.join(", ") : (doctor.languages || ""))),
                whatsapp_no: doctorFields.whatsapp_no || "",
                medical_license: doctorFields.medical_license || "",
                qualification: doctorFields.qualification || "",
                services: doctorFields.services || [{ serviceName: "", unitPrice: "", discount: "" }],
                clinic_id: clinicInfo.id || doctorFields.clinic_id || "",
                clinic_name: clinicInfo.clinic_name || "", // Auto-fill clinic name from API response
                selectedWeekdays: selectedDays,
                timingStart: "",
                timingEnd: "",
                isParameters: doctorFields.isParameters !== undefined ? Number(doctorFields.isParameters) : 0,
                speech_to_text: doctorFields.speech_to_text !== undefined ? Number(doctorFields.speech_to_text) : 0
                ,
                tax_percentage: doctorFields.tax_percentage ?? doctorFields.taxPercentage ?? doctor.tax_percentage ?? doctor.taxPercentage ?? 0
            });
        }
    }, [isOpen, doctor, toast]);

    useEffect(() => {
        if (isOpen && formData.id) {
            // Fetch doctor parameters from API
            const fetchDoctorParameters = async () => {
                try {
                    const response = await axiosInstance.get(`/api/parameters/fetchByDoctorId?doctor_id=${formData.id}`);
                    if (Array.isArray(response.data)) {
                        setDoctorParameters(response.data);
                        // Preselect parameters if formData.parameters exists
                        if (formData.parameters) {
                            const selected = response.data.filter(param => Object.keys(formData.parameters).includes(param.name)).map(param => ({
                                value: param.name,
                                label: param.unit ? `${param.name} (${param.unit})` : param.name,
                                id: param.id,
                                unit: param.unit
                            }));
                            setSelectedParameters(selected);
                        }
                    } else {
                        setDoctorParameters([]);
                        setSelectedParameters([]);
                    }
                } catch (error) {
                    setDoctorParameters([]);
                    setSelectedParameters([]);
                    console.error('Error fetching doctor parameters:', error);
                }
            };
            fetchDoctorParameters();
        } else {
            setDoctorParameters([]);
            setSelectedParameters([]);
        }
    }, [isOpen, formData.id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };

    // Handle single file input changes for uploads
    const handleFileChange = (e) => {
        const { name, files } = e.target;
        if (!name) return;
        setUploads(prev => ({ ...prev, [name]: files?.[0] || null }));
    };

    // Reuse upload helper from DoctorForm: uploads file to server and returns objectUrl
    const uploadFile = async (file) => {
        const form = new FormData();
        form.append('file', file);
        try {
            const res = await axiosInstance.post('/api/upload', form, {
                headers: { 'Content-Type': 'multipart/form-data' },
                maxBodyLength: Infinity,
            });
            return res?.data?.objectUrl || res?.data?.url || null;
        } catch (error) {
            console.error('File upload failed:', error);
            throw error;
        }
    };

    const handleUploadSubmit = async () => {
        if (!formData.id) {
            toast({
                title: 'Doctor ID missing',
                description: 'Please save basic info first',
                status: 'warning',
                duration: 3000,
                isClosable: true
            });
            return;
        }

        try {
            setIsUploadingFiles(true);
            const uploadKeys = ['doctor_signature', 'bar_code', 'qr_code', 'letterhead'];
            const filePromises = uploadKeys.map(async (key) => {
                const file = uploads[key];
                if (!file) return null;
                const url = await uploadFile(file);
                return [key, url];
            });

            const results = await Promise.allSettled(filePromises);
            const fileUrls = {};
            const errorsArr = [];

            for (const r of results) {
                if (!r || (r.status === 'fulfilled' && r.value === null)) continue;
                if (r.status === 'fulfilled') {
                    const [k, url] = r.value;
                    if (k && url) fileUrls[k] = url;
                } else {
                    errorsArr.push(r.reason);
                }
            }

            // For keys where user didn't upload new file, try to preserve existing URL from originalDoctorData or doctor
            const existingSource = originalDoctorData || doctor || {};
            const preserveKeys = uploadKeys.reduce((acc, k) => {
                // check nested doctor object as well
                const val = existingSource?.[k] || existingSource?.doctor?.[k] || null;
                acc[k] = val || null;
                return acc;
            }, {});

            const payload = {
                id: formData.id,
                letterhead: fileUrls.letterhead || preserveKeys.letterhead || null,
                doctor_signature: fileUrls.doctor_signature || preserveKeys.doctor_signature || null,
                qr_code: fileUrls.qr_code || preserveKeys.qr_code || null,
                bar_code: fileUrls.bar_code || preserveKeys.bar_code || null,
            };

            const uploadResponse = await axiosInstance.post('api/doctor/uploadFiles', payload, { headers: { 'Content-Type': 'application/json' } });

            setIsUploadsCompleted(true);
            toast({
                title: 'Uploads Submitted',
                description: 'Files uploaded and saved successfully',
                status: 'success',
                duration: 3000,
                isClosable: true
            });

            // If backend returned updated doctor, propagate to parent
            if (uploadResponse?.data) {
                onUpdate(uploadResponse.data);
            }
        } catch (error) {
            console.error('Error uploading files:', error);
            toast({
                title: 'Upload Error',
                description: error.response?.data?.message || 'Failed to upload files',
                status: 'error',
                duration: 4000,
                isClosable: true
            });
        } finally {
            setIsUploadingFiles(false);
        }
    };

    const handlePaymentChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            payment_details: {
                ...prev.payment_details,
                [name]: value
            }
        }));
    };

    const handleServiceChange = (index, field, value) => {
        const updatedServices = formData.services.map((service, i) =>
            i === index ? { ...service, [field]: value } : service
        );
        setFormData(prev => ({ ...prev, services: updatedServices }));
    };

    const addService = () => {
        setFormData(prev => ({
            ...prev,
            services: [...prev.services, { serviceName: "", unitPrice: "", discount: "" }]
        }));
    };

    const removeService = (index) => {
        if (formData.services.length > 1) {
            setFormData(prev => ({
                ...prev,
                services: prev.services.filter((_, i) => i !== index)
            }));
        }
    };

    const handleWeekdayToggle = (day) => {
        setFormData(prev => {
            const selected = prev.selectedWeekdays ? [...prev.selectedWeekdays] : [];
            const index = selected.indexOf(day);
            if (index > -1) {
                selected.splice(index, 1);
                // Clear timing for this day
                const newOpd = { ...prev.opd_timing };
                newOpd[day.toLowerCase()] = null;
                return { ...prev, selectedWeekdays: selected, opd_timing: newOpd };
            } else {
                selected.push(day);
                return { ...prev, selectedWeekdays: selected };
            }
        });
    };

    const handleSetTiming = () => {
        const { timingStart, timingEnd, selectedWeekdays, opd_timing } = formData;
        if (!timingStart || !timingEnd || !selectedWeekdays || selectedWeekdays.length === 0) {
            return;
        }
        const newTimings = { ...opd_timing };
        selectedWeekdays.forEach(day => {
            newTimings[day.toLowerCase()] = `${timingStart}-${timingEnd}`;
        });
        setFormData(prev => ({
            ...prev,
            opd_timing: newTimings,
            timingStart: "",
            timingEnd: "",
            selectedWeekdays: []
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        // Only validate if the field has been changed from original or is truly empty
        if (!formData.doctor && (!originalDoctorData || !originalDoctorData.doctor)) {
            newErrors.doctor = "Doctor name is required";
        }

        // For edit forms, we're more lenient - only validate if user is clearly trying to set a value
        // Most fields are optional in edit mode since we preserve original data

        // Validate tax percentage if provided
        if (formData.tax_percentage !== undefined && formData.tax_percentage !== null) {
            const tp = Number(formData.tax_percentage);
            if (Number.isNaN(tp) || tp < 0 || tp > 100) {
                newErrors.tax_percentage = "Tax % must be a number between 0 and 100.";
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            // Convert structured payment_details back to old format for API
            const { payment_details } = formData;
            let upi_details = '';
            let bank_details = '';

            if (payment_details) {
                // Build UPI details string
                if (payment_details.upi_id) {
                    upi_details = payment_details.upi_id;
                }

                // Build bank details string
                const bankParts = [];
                if (payment_details.bank_name) bankParts.push(payment_details.bank_name);
                if (payment_details.account_number) bankParts.push(`A/C: ${payment_details.account_number}`);
                if (payment_details.ifsc_code) bankParts.push(`IFSC: ${payment_details.ifsc_code}`);
                if (payment_details.account_holder) bankParts.push(`Holder: ${payment_details.account_holder}`);

                bank_details = bankParts.join(', ');
            }

            // Extract only the doctor fields from original data, not the entire nested object
            const originalDoctorFields = originalDoctorData?.doctor || originalDoctorData || {};

            // Handle services array - use form data if it has valid services, otherwise preserve original
            let servicesArray = formData.services;
            if (!servicesArray || servicesArray.length === 0 || (servicesArray.length === 1 && !servicesArray[0].serviceName)) {
                // If no valid services in form, try to preserve original services
                if (originalDoctorFields.services && Array.isArray(originalDoctorFields.services)) {
                    servicesArray = originalDoctorFields.services;
                } else {
                    // Fallback to empty array
                    servicesArray = [];
                }
            }

            // Build the update data but include only fields changed in the form
            const updateData = {};

            // id: include only if user changed it in the form
            // if (formData.id !== undefined && formData.id !== originalDoctorFields.id) {
                updateData.id = formData.id;
            // }

            if (formData.doctor !== undefined && formData.doctor !== originalDoctorFields.doctor) {
                updateData.doctor = formData.doctor;
            }

            if (formData.slot_duration !== undefined && formData.slot_duration !== originalDoctorFields.slot_duration) {
                updateData.slot_duration = formData.slot_duration;
            }

            if (formData.opd_timing !== undefined) {
                const formOpd = formData.opd_timing || {};
                const origOpd = originalDoctorFields.opd_timing || {};
                if (JSON.stringify(formOpd) !== JSON.stringify(origOpd)) {
                    updateData.opd_timing = formData.opd_timing;
                }
            }

            if (formData.emergency_charge !== undefined && formData.emergency_charge !== originalDoctorFields.emergency_charge) {
                updateData.emergency_charge = formData.emergency_charge;
            }

            if (formData.outside_opd_charge !== undefined && formData.outside_opd_charge !== originalDoctorFields.outside_opd_charge) {
                updateData.outside_opd_charge = formData.outside_opd_charge;
            }

            // payment details: only include when payment_details was present in the form (could be empty to clear)
            if (payment_details !== undefined) {
                if (upi_details !== originalDoctorFields.upi_details) {
                    updateData.upi_details = upi_details;
                }
                if (bank_details !== originalDoctorFields.bank_details) {
                    updateData.bank_details = bank_details;
                }
            }

            if (formData.language !== undefined) {
                const formLang = Array.isArray(formData.language) ? formData.language.join(', ') : (formData.language || "");
                const origLang = Array.isArray(originalDoctorFields.language) ? originalDoctorFields.language.join(', ') : (originalDoctorFields.language || "");
                if (formLang !== origLang) {
                    // send the raw form value (keeps array/string as provided)
                    updateData.language = formData.language;
                }
            }

            if (formData.whatsapp_no !== undefined && formData.whatsapp_no !== originalDoctorFields.whatsapp_no) {
                updateData.whatsapp_no = formData.whatsapp_no;
            }

            if (formData.qualification !== undefined && formData.qualification !== originalDoctorFields.qualification) {
                updateData.qualification = formData.qualification;
            }

            // services: only include when user provided services in form (even empty/cleared)
            if (formData.services !== undefined) {
                const origServices = Array.isArray(originalDoctorFields.services) ? originalDoctorFields.services : [];
                if (JSON.stringify(servicesArray) !== JSON.stringify(origServices)) {
                    updateData.services = servicesArray;
                }
            }

            if (formData.clinic_id !== undefined && formData.clinic_id !== originalDoctorFields.clinic_id) {
                updateData.clinic_id = formData.clinic_id;
            }

            if (formData.isParameters !== undefined && formData.isParameters !== (originalDoctorFields.isParameters || 0)) {
                updateData.isParameters = formData.isParameters;
            }

            if (formData.speech_to_text !== undefined && formData.speech_to_text !== (originalDoctorFields.speech_to_text || 0)) {
                updateData.speech_to_text = formData.speech_to_text;
            }

            if (formData.tax_percentage !== undefined) {
                const origTax = originalDoctorFields.tax_percentage ?? originalDoctorFields.taxPercentage ?? 0;
                if (formData.tax_percentage !== origTax) {
                    updateData.tax_percentage = formData.tax_percentage;
                }
            }

            if (formData.medical_license != originalDoctorFields.medical_license) {
                updateData.medical_license = formData.medical_license;
            }

            console.log('=== API CALL BODY ===');
            console.log(JSON.stringify(updateData, null, 2));
            console.log('=== END API CALL BODY ===');

            const response = await axiosInstance.post(API_ENDPOINTS.DOCTOR.UPDATE, updateData);

            if (response.data) {
                toast({
                    title: "Success",
                    description: "Doctor updated successfully",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                onUpdate(response.data);
                onClose();
            }
        } catch (error) {
            console.error("Error updating doctor:", error);
            const serverMessage = error.response?.data?.error || error.response?.data?.message || error.message;
            const { message: humanMessage, field } = humanizeApiError(API_ENDPOINTS.DOCTOR.UPDATE, serverMessage);

            if (field) {
                setErrors(prev => ({ ...prev, [field]: humanMessage }));
            }

            toast({
                title: "Error",
                description: humanMessage || error.response?.data?.message || "Failed to update doctor",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent maxW="800px">
                <ModalHeader>Edit Doctor</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <form onSubmit={handleSubmit}>
                        <VStack spacing={4} align="stretch">
                            {/* Basic Information  */}
                            <Text fontSize="lg" fontWeight="semibold">Basic Information</Text>

                            {/* Clinic Information - Auto-filled and read-only */}
                            <FormControl>
                                <FormLabel>Clinic</FormLabel>
                                <Input
                                    value={formData.clinic_name || 'No clinic assigned'}
                                    isReadOnly
                                    bg="gray.50"
                                    color="gray.700"
                                    placeholder="Clinic information will be auto-filled"
                                />
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                    Clinic is automatically assigned and cannot be changed
                                </Text>
                            </FormControl>

                            <HStack spacing={4}>
                                <FormControl isInvalid={!!errors.doctor}>
                                    <FormLabel>Doctor Name</FormLabel>
                                    <Input
                                        name="doctor"
                                        value={formData.doctor}
                                        onChange={handleChange}
                                        placeholder="Enter doctor name"
                                    />
                                    <FormErrorMessage>{errors.doctor}</FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={!!errors.slot_duration}>
                                    <FormLabel>Slot Duration (minutes)</FormLabel>
                                    <Input
                                        name="slot_duration"
                                        value={formData.slot_duration}
                                        onChange={handleChange}
                                        placeholder="20"
                                        type="text"
                                    />
                                    <FormErrorMessage>{errors.slot_duration}</FormErrorMessage>
                                </FormControl>
                            </HStack>

                            <HStack spacing={4}>
                                <FormControl isInvalid={!!errors.emergency_charge}>
                                    <FormLabel>Emergency Charge</FormLabel>
                                    <Input
                                        name="emergency_charge"
                                        value={formData.emergency_charge}
                                        onChange={handleChange}
                                        placeholder="600"
                                        type="text"
                                    />
                                    <FormErrorMessage>{errors.emergency_charge}</FormErrorMessage>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Outside OPD Charge</FormLabel>
                                    <Input
                                        name="outside_opd_chaxrge"
                                        value={formData.outside_opd_charge}
                                        onChange={handleChange}
                                        placeholder="500"
                                        type="text"
                                    />
                                </FormControl>

                                <FormControl isInvalid={!!errors.tax_percentage}>
                                    <FormLabel>Tax %</FormLabel>
                                    <NumberInput value={formData.tax_percentage} min={0} max={100}>
                                        <NumberInputField
                                            name="tax_percentage"
                                            value={formData.tax_percentage}
                                            onChange={handleChange}
                                        />
                                    </NumberInput>
                                    <FormErrorMessage>{errors.tax_percentage}</FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={!!errors.whatsapp_no}>
                                    <FormLabel>WhatsApp Number</FormLabel>
                                    <Input
                                        name="whatsapp_no"
                                        value={formData.whatsapp_no}
                                        onChange={handleChange}
                                        placeholder="+919999998888"
                                    />
                                    <FormErrorMessage>{errors.whatsapp_no}</FormErrorMessage>
                                </FormControl>
                            </HStack>

                            <HStack spacing={4}>
                                <FormControl isInvalid={!!errors.medical_license}>
                                    <FormLabel>Medical License</FormLabel>
                                    <Input
                                        name="medical_license"
                                        value={formData.medical_license}
                                        onChange={handleChange}
                                        placeholder="MED7654321IN"
                                    />
                                    <FormErrorMessage>{errors.medical_license}</FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={!!errors.qualification}>
                                    <FormLabel>Qualification</FormLabel>
                                    <Input
                                        name="qualification"
                                        value={formData.qualification}
                                        onChange={handleChange}
                                        placeholder="MBBS, DNB (Cardiology)"
                                    />
                                    <FormErrorMessage>{errors.qualification}</FormErrorMessage>
                                </FormControl>
                            </HStack>

                            <FormControl>
                                <FormLabel>Languages</FormLabel>
                                <Input
                                    name="language"
                                    value={formData.language}
                                    onChange={handleChange}
                                    placeholder="English, Hindi, Spanish"
                                />
                            </FormControl>

                            {/* Password Update Section */}
                            <Divider my={6} />
                            <Text fontSize="lg" fontWeight="semibold">Update Password</Text>
                            <FormControl>
                                <FormLabel>New Password</FormLabel>
                                <Flex>
                                    <Input
                                        type={showPassword ? "text" : "password"}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Enter new password"
                                        flex="1"
                                    />
                                    <IconButton
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                        icon={showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                                        onClick={() => setShowPassword(!showPassword)}
                                        size="sm"
                                        ml={2}
                                    />
                                </Flex>
                            </FormControl>
                            <Button
                                mt={2}
                                colorScheme="blue"
                                onClick={async () => {
                                    if (!newPassword) {
                                        toast({
                                            title: 'Error',
                                            description: 'Please enter a new password',
                                            status: 'error',
                                            duration: 3000,
                                            isClosable: true,
                                        });
                                        return;
                                    }
                                    try {
                                        const response = await axiosInstance.post('/api/doctor/updatePassword', {
                                            doctor_id: formData.id,
                                            new_password: newPassword
                                        });
                                        toast({
                                            title: 'Success',
                                            description: response.data?.message || 'Password updated successfully',
                                            status: 'success',
                                            duration: 3000,
                                            isClosable: true,
                                        });
                                        setNewPassword('');
                                    } catch (error) {
                                        console.error('Error updating password:', error);
                                        toast({
                                            title: 'Error',
                                            description: error.response?.data?.message || 'Failed to update password',
                                            status: 'error',
                                            duration: 5000,
                                            isClosable: true,
                                        });
                                    }
                                }}
                            >
                                Update Password
                            </Button>

                            <Divider my={6} />

                            {/* Consultation Settings - Advice and Parameters */}
                            <Text fontSize="lg" fontWeight="semibold">Consultation Settings</Text>

                            <FormControl>
                                <FormLabel>Parameters for Patient</FormLabel>
                                {/* Parameters and Speech to Text toggles */}
                                <Box mb={4} p={4} borderWidth={1} borderColor="gray.400" borderRadius="md">
                                    <Flex align="center" gap={6}>
                                        <Flex align="center" gap={2}>
                                            <Text fontWeight="medium">Parameters:</Text>
                                            <Button
                                                size="sm"
                                                colorScheme={formData.isParameters === 1 ? "green" : "gray"}
                                                onClick={() => {
                                                    const newValue = formData.isParameters === 1 ? 0 : 1;
                                                    setFormData(prev => ({ ...prev, isParameters: newValue }));
                                                    toast({
                                                        title: "Parameters Toggle",
                                                        description: `Parameters functionality ${newValue === 1 ? 'enabled' : 'disabled'}`,
                                                        status: "info",
                                                        duration: 2000,
                                                        isClosable: true,
                                                    });
                                                }}
                                            >
                                                {formData.isParameters === 1 ? "Enabled" : "Disabled"}
                                            </Button>
                                        </Flex>
                                        <Flex align="center" gap={2}>
                                            <Text fontWeight="medium">Speech to Text:</Text>
                                            <Button
                                                size="sm"
                                                colorScheme={formData.speech_to_text === 1 ? "green" : "gray"}
                                                onClick={() => {
                                                    const newValue = formData.speech_to_text === 1 ? 0 : 1;
                                                    setFormData(prev => ({ ...prev, speech_to_text: newValue }));
                                                    toast({
                                                        title: "Speech to Text Toggle",
                                                        description: `Speech to text functionality ${newValue === 1 ? 'enabled' : 'disabled'}`,
                                                        status: "info",
                                                        duration: 2000,
                                                        isClosable: true,
                                                    });
                                                }}
                                            >
                                                {formData.speech_to_text === 1 ? "Enabled" : "Disabled"}
                                            </Button>
                                        </Flex>
                                    </Flex>
                                </Box>

                                {/* CSV Upload Section for Parameters */}
                                <Box mb={4}>
                                    <Text fontSize="sm" fontWeight="medium" mb={2}>Upload Parameters CSV File</Text>
                                    <Flex gap={2} align="center">
                                        <FileUploadField
                                            id="parameters-csv-file-input-edit"
                                            name="parameters_csv"
                                            onChange={() => {
                                            }}
                                            accept=".csv"
                                            isDisabled={!formData.clinic_id}
                                            helperText="Upload CSV with one parameter per row"
                                        />
                                        <Button
                                            onClick={async () => {
                                                const fileInput = document.getElementById('parameters-csv-file-input-edit');
                                                const file = fileInput?.files[0];

                                                if (!file) {
                                                    toast({
                                                        title: 'No file selected',
                                                        description: 'Please select a CSV file to upload',
                                                        status: 'warning',
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });
                                                    return;
                                                }

                                                if (!formData.clinic_id) {
                                                    toast({
                                                        title: 'Clinic Required',
                                                        description: 'Please select a clinic first',
                                                        status: 'warning',
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });
                                                    return;
                                                }

                                                // Check if doctor_id is available
                                                if (!formData.id) {
                                                    toast({
                                                        title: 'Doctor ID Required',
                                                        description: 'Doctor ID is missing. Please refresh and try again.',
                                                        status: 'warning',
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });
                                                    return;
                                                }

                                                try {
                                                    // Create FormData object with required parameters including doctor_id
                                                    const formDataObj = new FormData();
                                                    formDataObj.append('file', file);
                                                    formDataObj.append('clinic_id', formData.clinic_id);
                                                    formDataObj.append('doctor_id', formData.id); // Add doctor_id to fix the 400 error

                                                    const response = await axiosInstance.post(
                                                        '/api/parameters/uploadParams',
                                                        formDataObj,
                                                        { headers: { 'Content-Type': 'multipart/form-data' } }
                                                    );

                                                    toast({
                                                        title: 'Upload Successful',
                                                        description: 'Patient parameters have been uploaded successfully',
                                                        status: 'success',
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });

                                                    if (fileInput) fileInput.value = '';
                                                } catch (error) {
                                                    console.error('Error uploading parameters CSV file:', error);
                                                    toast({
                                                        title: 'Upload Failed',
                                                        description: error.response?.data?.message || 'Failed to upload parameters CSV file',
                                                        status: 'error',
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });
                                                }
                                            }}
                                            colorScheme="blue"
                                            isDisabled={!formData.clinic_id}
                                        >
                                            Upload
                                        </Button>
                                    </Flex>
                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                        Upload a CSV file containing patient parameters. The file should have parameters
                                        text in each row.
                                    </Text>
                                </Box>

                                {/* After the parameters CSV upload section, add dynamic parameter fetch and display: */}
                                <Box mb={4}>
                                    <Text fontSize="sm" fontWeight="medium" mb={2}>Select Parameters (Dynamic)</Text>
                                    <ReactSelect
                                        isMulti
                                        name="parameters_dynamic"
                                        options={doctorParameters.map(param => ({
                                            value: param.name,
                                            label: param.unit ? `${param.name} (${param.unit})` : param.name,
                                            id: param.id,
                                            unit: param.unit
                                        }))}
                                        value={selectedParameters}
                                        onChange={selected => {
                                            setSelectedParameters(selected);
                                            setFormData(prev => ({
                                                ...prev,
                                                parameters: selected.reduce((acc, curr) => {
                                                    acc[curr.value] = prev.parameters?.[curr.value] || '';
                                                    return acc;
                                                }, {})
                                            }));
                                        }}
                                        placeholder={formData.id ? (doctorParameters.length > 0 ? "Select parameters..." : "No parameters found") : "No doctor ID"}
                                        isDisabled={!formData.id}
                                    />
                                    {doctorParameters.length > 0 && (
                                        <Text fontSize="xs" color="green.600" mt={1}>
                                            Loaded {doctorParameters.length} parameters for this doctor
                                        </Text>
                                    )}
                                    {formData.id && (
                                        <Button
                                            size="xs"
                                            onClick={async () => {
                                                try {
                                                    const response = await axiosInstance.get(`/api/parameters/fetchByDoctorId?doctor_id=${formData.id}`);
                                                    if (Array.isArray(response.data)) {
                                                        setDoctorParameters(response.data);
                                                        toast({
                                                            title: 'Parameters Refreshed',
                                                            description: 'Parameter list has been updated',
                                                            status: 'success',
                                                            duration: 2000,
                                                            isClosable: true,
                                                        });
                                                    } else {
                                                        setDoctorParameters([]);
                                                        toast({
                                                            title: 'Error',
                                                            description: 'No parameters found',
                                                            status: 'error',
                                                            duration: 3000,
                                                            isClosable: true,
                                                        });
                                                    }
                                                } catch (error) {
                                                    setDoctorParameters([]);
                                                    toast({
                                                        title: 'Error',
                                                        description: 'Failed to refresh parameters',
                                                        status: 'error',
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });
                                                }
                                            }}
                                            colorScheme="blue"
                                            variant="outline"
                                            mt={2}
                                        >
                                            Refresh Parameters
                                        </Button>
                                    )}
                                </Box>
                                {/* Render input fields for each selected parameter (dynamic) */}
                                {selectedParameters.map(param => (
                                    <Box key={param.id} mb={2}>
                                        <FormLabel fontSize="sm">{param.label}</FormLabel>
                                        <Input
                                            name={param.value}
                                            value={formData.parameters?.[param.value] || ''}
                                            onChange={e => {
                                                const { name, value } = e.target;
                                                setFormData(prev => ({
                                                    ...prev,
                                                    parameters: {
                                                        ...prev.parameters,
                                                        [name]: value
                                                    }
                                                }));
                                            }}
                                            placeholder={`Enter value${param.unit ? ` (${param.unit})` : ''}`}
                                        />
                                    </Box>
                                ))}
                            </FormControl>

                            {/* Advices Section - CSV Upload and Dynamic Select */}
                            <Divider my={6} />
                            <Text fontSize="lg" fontWeight="semibold">Advices Settings</Text>

                            <FormControl>
                                <FormLabel>Advices for Patient</FormLabel>

                                {/* CSV Upload Section for Advices */}
                                <Box mb={4}>
                                    <Text fontSize="sm" fontWeight="medium" mb={2}>Upload Advices CSV File</Text>
                                    <Flex gap={2} align="center">
                                        <FileUploadField
                                            id="advices-csv-file-input-edit"
                                            name="advices_csv"
                                            onChange={() => {
                                            }}
                                            accept=".csv"
                                            isDisabled={!formData.clinic_id}
                                            helperText="CSV with advice text per row"
                                        />
                                        <Button
                                            onClick={async () => {
                                                const fileInput = document.getElementById('advices-csv-file-input-edit');
                                                const file = fileInput?.files[0];

                                                if (!file) {
                                                    toast({
                                                        title: 'No file selected',
                                                        description: 'Please select a CSV file to upload',
                                                        status: 'warning',
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });
                                                    return;
                                                }

                                                if (!formData.clinic_id) {
                                                    toast({
                                                        title: 'Clinic Required',
                                                        description: 'Please select a clinic first',
                                                        status: 'warning',
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });
                                                    return;
                                                }

                                                // Check if doctor_id is available
                                                if (!formData.id) {
                                                    toast({
                                                        title: 'Doctor ID Required',
                                                        description: 'Doctor ID is missing. Please refresh and try again.',
                                                        status: 'warning',
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });
                                                    return;
                                                }

                                                try {
                                                    // Create FormData object with required parameters including doctor_id
                                                    const formDataObj = new FormData();
                                                    formDataObj.append('file', file);
                                                    formDataObj.append('clinic_id', formData.clinic_id);
                                                    formDataObj.append('doctor_id', formData.id); // Add doctor_id to fix the 400 error

                                                    const response = await axiosInstance.post(
                                                        '/api/prescription/uploadAdvices',
                                                        formDataObj,
                                                        { headers: { 'Content-Type': 'multipart/form-data' } }
                                                    );

                                                    toast({
                                                        title: 'Upload Successful',
                                                        description: 'Patient advices have been uploaded successfully',
                                                        status: 'success',
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });

                                                    if (fileInput) fileInput.value = '';
                                                } catch (error) {
                                                    console.error('Error uploading advices CSV file:', error);
                                                    toast({
                                                        title: 'Upload Failed',
                                                        description: error.response?.data?.message || 'Failed to upload advices CSV file',
                                                        status: 'error',
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });
                                                }
                                            }}
                                            colorScheme="blue"
                                            isDisabled={!formData.clinic_id}
                                        >
                                            Upload
                                        </Button>
                                    </Flex>
                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                        Upload a CSV file containing patient advices. The file should have advices text
                                        in each row.
                                    </Text>
                                </Box>

                                {/* After the advices CSV upload section, add dynamic advice fetch and display: */}
                                <Box mb={4}>
                                    <Text fontSize="sm" fontWeight="medium" mb={2}>Select Advices</Text>
                                    <ReactSelect
                                        isMulti
                                        name="advices"
                                        options={Array.isArray(formData.dynamicAdvices) ? formData.dynamicAdvices : []}
                                        value={formData.selectedAdvices || []}
                                        onChange={selected => {
                                            setFormData(prev => ({
                                                ...prev,
                                                selectedAdvices: selected
                                            }));
                                        }}
                                        placeholder={formData.clinic_id ? (Array.isArray(formData.dynamicAdvices) && formData.dynamicAdvices.length > 0 ? "Select advises..." : "No advises found") : "Select clinic first"}
                                        isDisabled={!formData.clinic_id}
                                    />
                                    {Array.isArray(formData.dynamicAdvices) && formData.dynamicAdvices.length > 0 && (
                                        <Text fontSize="xs" color="green.600" mt={1}>
                                            Loaded {formData.dynamicAdvices.length} advises from selected clinic
                                        </Text>
                                    )}
                                    {formData.clinic_id && (
                                        <Button
                                            size="xs"
                                            onClick={async () => {
                                                try {
                                                    // alert(JSON.stringify(doctor || null))
                                                    const response = await axiosInstance.get(`/api/prescription/getAdvices?doctor_id=${doctor.id}`);
                                                    let advicesArray = [];
                                                    if (response.data && response.data.data && Array.isArray(response.data.data)) {
                                                        advicesArray = response.data.data;
                                                    } else if (response.data && response.data.advices) {
                                                        advicesArray = response.data.advices;
                                                    } else if (Array.isArray(response.data)) {
                                                        advicesArray = response.data;
                                                    } else {
                                                        advicesArray = [];
                                                    }
                                                    const transformedAdvices = advicesArray.map(advice => ({
                                                        value: advice.advice_text || advice.advice || advice.text || 'No advise text',
                                                        label: advice.advice_text || advice.advice || advice.text || 'No advise text',
                                                        id: advice.id
                                                    }));
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        dynamicAdvices: transformedAdvices
                                                    }));
                                                    toast({
                                                        title: 'Advices Refreshed',
                                                        description: 'Advice list has been updated',
                                                        status: 'success',
                                                        duration: 2000,
                                                        isClosable: true,
                                                    });
                                                } catch (error) {
                                                    console.error('Error fetching advises:', error);
                                                    toast({
                                                        title: 'Error',
                                                        description: 'Failed to refresh advices',
                                                        status: 'error',
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });
                                                }
                                            }}
                                            colorScheme="blue"
                                            variant="outline"
                                            mt={2}
                                        >
                                            Refresh Advises
                                        </Button>
                                    )}
                                </Box>
                            </FormControl>

                            {/* Payment Details - Moved here as per reference code */}
                            <Divider my={6} />
                            <Text fontSize="lg" fontWeight="semibold">Payment Details</Text>
                            <Flex gap={2} mb={2}>
                                <Box flex="1">
                                    <FormControl>
                                        <FormLabel>Account Holder Name</FormLabel>
                                        <Input name="account_holder" value={formData.payment_details.account_holder}
                                            onChange={handlePaymentChange} />
                                    </FormControl>
                                </Box>
                                <Box flex="1">
                                    <FormControl>
                                        <FormLabel>Account Number</FormLabel>
                                        <Input name="account_number" value={formData.payment_details.account_number}
                                            onChange={handlePaymentChange} />
                                    </FormControl>
                                </Box>
                            </Flex>
                            <Flex gap={2} mb={2}>
                                <Box flex="1">
                                    <FormControl>
                                        <FormLabel>Bank Name</FormLabel>
                                        <Input name="bank_name" value={formData.payment_details.bank_name}
                                            onChange={handlePaymentChange} />
                                    </FormControl>
                                </Box>
                                <Box flex="1">
                                    <FormControl>
                                        <FormLabel>IFSC Code</FormLabel>
                                        <Input name="ifsc_code" value={formData.payment_details.ifsc_code}
                                            onChange={handlePaymentChange} />
                                    </FormControl>
                                </Box>
                            </Flex>
                            <Flex gap={2} mb={2}>
                                <Box flex="1">
                                    <FormControl>
                                        <FormLabel>UPI ID</FormLabel>
                                        <Input name="upi_id" value={formData.payment_details.upi_id}
                                            onChange={handlePaymentChange} />
                                    </FormControl>
                                </Box>
                            </Flex>

                            {/* Files & Uploads */}
                            <Divider my={6} />
                            <Text fontSize="lg" fontWeight="semibold">Files</Text>
                            <Text fontSize="sm" color="gray.600" mb={2}>Preview current files and upload
                                replacements</Text>
                            <Box mb={4}>
                                {['doctor_signature', 'bar_code', 'qr_code', 'letterhead'].map((key) => {
                                    // try multiple locations for existing url
                                    const existingUrl = originalDoctorData?.[key] || originalDoctorData?.doctor?.[key] || doctor?.[key] || doctor?.doctor?.[key] || null;
                                    return (
                                        <FormControl key={key} mb={3}>
                                            <FormLabel textTransform="capitalize">{key.replace('_', ' ')}</FormLabel>
                                            <Flex gap={3} align="center">
                                                <FileUploadField
                                                    id={`edit-file-${key}`}
                                                    name={key}
                                                    onChange={handleFileChange}
                                                    selectedFile={uploads[key]}
                                                />
                                                <Box>
                                                    {uploads[key] ? (
                                                        <Text fontSize="sm">New file ready to
                                                            upload: {uploads[key].name}</Text>
                                                    ) : existingUrl ? (
                                                        <a href={existingUrl} target="_blank" rel="noreferrer">
                                                            {existingUrl.match(/\.(png|jpe?g|gif|webp)$/i) ? (
                                                                <img src={existingUrl} alt={key} style={{
                                                                    maxWidth: 120,
                                                                    maxHeight: 80,
                                                                    objectFit: 'contain'
                                                                }} />
                                                            ) : (
                                                                <Text fontSize="sm" color="blue.600">View file</Text>
                                                            )}
                                                        </a>
                                                    ) : (
                                                        <Text fontSize="sm" color="gray.500">No file</Text>
                                                    )}
                                                </Box>
                                            </Flex>
                                        </FormControl>
                                    );
                                })}

                                <Flex gap={3}>
                                    <Button colorScheme="blue" onClick={handleUploadSubmit} isLoading={isUploadingFiles}
                                        isDisabled={!formData.id}>
                                        Submit Uploads
                                    </Button>
                                    <Button variant="outline" onClick={() => {
                                        setIsUploadsCompleted(true);
                                        toast({
                                            title: 'Skipped uploads',
                                            description: 'Doctor update completed without changing files',
                                            status: 'info',
                                            duration: 2500,
                                            isClosable: true
                                        });
                                    }}>
                                        Skip Uploads
                                    </Button>
                                </Flex>
                                {isUploadsCompleted && (
                                    <Alert status="success" mt={4}><AlertIcon />Uploads completed successfully.</Alert>
                                )}
                            </Box>

                            {/* OPD Timing */}
                            <Divider my={6} />
                            <Box>
                                <Text fontSize="lg" fontWeight="semibold" mb={4}>OPD Timing</Text>
                                <FormControl isInvalid={!!errors.opd_timing}>
                                    <FormLabel>Select Weekdays</FormLabel>
                                    <Flex wrap="wrap" gap={3} mb={2}>
                                        {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => (
                                            <Box key={day} display="flex" alignItems="center" gap={1} width="120px">
                                                <input
                                                    type="checkbox"
                                                    id={`weekday-checkbox-${day}`}
                                                    checked={formData.selectedWeekdays?.includes(day)}
                                                    onChange={() => handleWeekdayToggle(day)}
                                                />
                                                <label htmlFor={`weekday-checkbox-${day}`}>{day}</label>
                                            </Box>
                                        ))}
                                    </Flex>
                                    {formData.selectedWeekdays?.length > 0 && (
                                        <Flex wrap="wrap" gap={2} mb={3}>
                                            {formData.selectedWeekdays.map((day) => (
                                                <Flex
                                                    key={day}
                                                    align="center"
                                                    bg="gray.200"
                                                    px={2}
                                                    py={1}
                                                    borderRadius="md"
                                                    gap={1}
                                                >
                                                    <Text>{day}</Text>
                                                    <IconButton
                                                        icon={<div style={{ fontSize: "1rem", color: "white" }}>X</div>}
                                                        size="xs"
                                                        colorScheme="red"
                                                        aria-label={`Remove ${day}`}
                                                        onClick={() => handleWeekdayToggle(day)}
                                                    />
                                                </Flex>
                                            ))}
                                        </Flex>
                                    )}
                                    <Flex gap={2} align="center" mb={3}>
                                        <Input
                                            type="time"
                                            name="timingStart"
                                            value={formData.timingStart}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                timingStart: e.target.value
                                            }))
                                            }
                                            placeholder="Start Time"
                                            width="120px"
                                        />
                                        <Text>to</Text>
                                        <Input
                                            type="time"
                                            name="timingEnd"
                                            value={formData.timingEnd}
                                            onChange={(e) => setFormData(prev => ({ ...prev, timingEnd: e.target.value }))
                                            }
                                            placeholder="End Time"
                                            width="120px"
                                        />
                                        <Button
                                            size="sm"
                                            colorScheme="blue"
                                            onClick={handleSetTiming}
                                        >
                                            Set Timing for Selected Days
                                        </Button>
                                    </Flex>
                                    {Object.entries(formData.opd_timing)
                                        .filter(([_, timing]) => timing && timing !== "null")
                                        .map(([day, timing]) => (
                                            <Box key={day} mb={3} borderWidth="1px" borderColor="gray.200"
                                                borderRadius="md" p={3}>
                                                <Text fontWeight="bold" mb={2} textTransform="capitalize">{day}</Text>
                                                <Flex gap={2} align="center">
                                                    <Text fontSize="sm">{timing}</Text>
                                                    <IconButton
                                                        aria-label="Remove timing"
                                                        icon={<div style={{ fontSize: "1rem", color: "white" }}>X</div>}
                                                        size="xs"
                                                        colorScheme="red"
                                                        borderColor="white"
                                                        onClick={() => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                opd_timing: {
                                                                    ...prev.opd_timing,
                                                                    [day]: null
                                                                }
                                                            }));
                                                        }}
                                                    />
                                                </Flex>
                                            </Box>
                                        ))}
                                    <FormErrorMessage>{errors.opd_timing}</FormErrorMessage>
                                </FormControl>
                            </Box>

                            {/* Services */}
                            <Divider my={6} />
                            <Flex justifyContent="space-between" alignItems="center">
                                <Text fontSize="lg" fontWeight="semibold">Services</Text>
                                {isLoadingServices && (
                                    <Text fontSize="sm" color="gray.500">Loading services...</Text>
                                )}
                            </Flex>

                            {formData.services.map((service, index) => (
                                <HStack key={index} spacing={4}>
                                    <FormControl>
                                        <FormLabel>Service Name</FormLabel>
                                        <Input
                                            value={service.serviceName}
                                            onChange={(e) => handleServiceChange(index, "serviceName", e.target.value)}
                                            placeholder="Service name"
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Unit Price</FormLabel>
                                        <Input
                                            type="number"
                                            value={service.unitPrice}
                                            onChange={(e) => handleServiceChange(index, "unitPrice", e.target.value)}
                                            placeholder="Price"
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Discount (%)</FormLabel>
                                        <Input
                                            type="number"
                                            value={service.discount}
                                            onChange={(e) => handleServiceChange(index, "discount", e.target.value)}
                                            placeholder="Discount"
                                        />
                                    </FormControl>
                                    {formData.services.length > 1 && (
                                        <IconButton
                                            icon={<div style={{ fontSize: "1rem", color: "white" }}>X</div>}
                                            onClick={() => removeService(index)}
                                            border="none"
                                            colorScheme="red"
                                            size="sm"
                                            aria-label="Remove service"
                                            alignSelf="flex-end"
                                            mb={4}
                                            justifySelf="flex-end"
                                        />
                                    )}
                                </HStack>
                            ))}
                            <Button onClick={addService} colorScheme="blue" size="sm" alignSelf="flex-start">
                                Add Service
                            </Button>
                        </VStack>


                        <Button
                            mt={6}
                            colorScheme="blue"
                            type="submit"
                            isLoading={isSubmitting}
                            w="full"
                        >
                            Update Doctor
                        </Button>
                    </form>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
